import React, { useState, useEffect, useRef } from 'react';
import OsScroll from './ui/OsScroll';
import { Modal } from './ui/Modal';
import { Save, Lock, AlertCircle, Loader, Boxes, Folder, Users } from 'lucide-react';
import { ShareRequestRow } from './ShareMessageCard';
import './Chat.css';
import CompactSelectPicker from './ui/CompactSelectPicker';
import shareService from '../services/shareService';
import { collectionService } from '../services/collectionService';
import { acceptDecryptedShareIntoCloud } from '../services/shareCloudAccept';
import {
  ShareEnvelopeError,
  decryptSnapshotData,
  isEncryptedSnapshotData,
} from '../utils/shareEnvelope';
import {
  loadSnapshot,
  saveSnapshot,
  saveLocalWorkspaceManifest,
  listLocalWorkspaceMetasFromManifest,
} from '../services/localWorkspaceStore';
import { generateLocalWorkspaceId } from '../utils/workspaceStorage';
import { requireWorkspaceSlot, workspaceSlotRefusal } from '../plans/localWorkspaces';
import { apiRoomRefusal, requireApiRoom } from '../plans/localApiUsage';
import { countApiItems } from '../plans/apiCounts';

/**
 * Plans v2: what is `add` ({ collections, requests } counts) must fit next to
 * what every own local workspace already holds (Free: 2 collections, 20
 * requests on this machine). The TARGET local workspace counts by the
 * snapshot this writes onto (`target`: { id, collections }; none for a new
 * workspace). Refused, the upgrade opens and this throws with the sentence.
 */
async function requireLocalApiRoom(add, target = { id: null, collections: [] }) {
  const refused = await apiRoomRefusal(add, { target });
  if (refused && !(await requireApiRoom(add, { target }))) {
    throw Object.assign(new Error(refused), { planLimit: true });
  }
}


const TYPE_LABELS = {
  team:       'Team',
  workspace:  'Workspace',
  collection: 'Collection',
  request:    'Request',
};

const MODAL_TITLES = {
  team:       'Join Team',
  workspace:  'Save Workspace',
  collection: 'Save Collection',
  request:    'Save Request',
};

// "New workspace…" destination — the kind (local / cloud / team) is chosen via
// radio buttons. Workspace shares always use this (a workspace fork IS a new
// workspace); collection/request shares offer it alongside existing workspaces.
const NEW_WS = '__new_ws__';
// Request-share destination collection sentinels
const AUTO_COLLECTION = '__auto__'; // server find-or-creates "Shared Requests"
const NEW_COLLECTION = '__new__';

function isLocalWs(w) {
  return w.storage === 'local' || w.isLocal === true;
}

const ShareAcceptModal = ({
  isOpen,
  onClose,
  token,
  // Chat share cards pass the conversation they were rendered in, and the
  // `enc` marker the sender stamped on the card. A deep link has neither: a
  // deep-linked share is never a chat snapshot, so it is never encrypted.
  channelId = null,
  encHint = null,
  workspaces = [],
  teams = [],
  isAuthenticated = false,
  showToast,
  onWorkspaceAdded,
}) => {
  const [info, setInfo]               = useState(null);
  const [fetchError, setFetchError]   = useState('');
  const [fetching, setFetching]       = useState(false);

  const [password, setPassword]       = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordOk, setPasswordOk]   = useState(false);

  const [workspaceId, setWorkspaceId] = useState('');
  const [accepting, setAccepting]     = useState(false);
  const [accepted, setAccepted]       = useState(false);
  // Set when the snapshot could not be opened. The card above it still renders
  // (its label, rows and counts come from the share INFO, not the payload), so
  // the recipient sees what was shared and that they cannot open it — the
  // honest state, and the same shape `EncryptedAttachment` uses for an image.
  const [decryptError, setDecryptError] = useState('');

  // Request shares: destination collection inside the chosen workspace.
  const [collectionChoice, setCollectionChoice] = useState(AUTO_COLLECTION);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [wsCollections, setWsCollections] = useState([]);
  const [loadingCols, setLoadingCols] = useState(false);
  // "New workspace…" destination (collection/request shares)
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWsKind, setNewWsKind] = useState('cloud'); // 'cloud' | 'local' | 'team'
  const [newWsTeamId, setNewWsTeamId] = useState('');

  // Fetch share info whenever token changes
  useEffect(() => {
    if (!isOpen || !token) return;
    setInfo(null);
    setFetchError('');
    setPassword('');
    setPasswordError('');
    setPasswordOk(false);
    setWorkspaceId('');
    setAccepted(false);
    setDecryptError('');
    setCollectionChoice(AUTO_COLLECTION);
    setNewCollectionName('');
    setWsCollections([]);
    setNewWorkspaceName('');
    setNewWsKind(isAuthenticated ? 'cloud' : 'local');
    setNewWsTeamId('');

    setFetching(true);
    shareService.getShareInfo(token)
      .then((data) => {
        setInfo(data);
        if (!data.password_protected) setPasswordOk(true);
      })
      .catch((err) => setFetchError(err.message))
      .finally(() => setFetching(false));
  }, [isOpen, token, isAuthenticated]);

  // Auto-select the destination. A workspace share always lands in a NEW
  // workspace (kind picked via radios), pre-named after the share; for
  // collection/request shares default to the first existing workspace.
  useEffect(() => {
    if (!info || (info.type !== 'collection' && info.type !== 'workspace' && info.type !== 'request')) return;
    if (workspaceId) return;

    if (info.type === 'workspace') {
      setWorkspaceId(NEW_WS);
      setNewWorkspaceName((prev) => prev || info.name || '');
      return;
    }

    const cloudWs = workspaces.filter((w) => w.id && !isLocalWs(w));
    if (cloudWs.length > 0) {
      setWorkspaceId(String(cloudWs[0].id));
    } else {
      // Collection/request: default to first local workspace if available
      const localWs = workspaces.filter((w) => w.id && isLocalWs(w));
      if (localWs.length > 0) setWorkspaceId(String(localWs[0].id));
    }
  }, [info, workspaces, workspaceId]);

  // Request shares into a cloud workspace: offer that workspace's collections
  // as the destination (plus auto "Shared Requests" and "New collection…").
  // The user's picked destination only resets when the WORKSPACE changes — a
  // background workspaces-list refresh must not discard it.
  const prevColsWsRef = useRef(null);
  useEffect(() => {
    if (prevColsWsRef.current !== workspaceId) {
      prevColsWsRef.current = workspaceId;
      setCollectionChoice(AUTO_COLLECTION);
      setNewCollectionName('');
    }
    if (!info || info.type !== 'request' || !workspaceId || workspaceId === NEW_WS) {
      setWsCollections([]);
      return;
    }
    const selectedWs = workspaces.find((w) => String(w.id) === workspaceId);
    if (!selectedWs || isLocalWs(selectedWs)) {
      setWsCollections([]);
      return;
    }
    let stale = false;
    setLoadingCols(true);
    collectionService
      .getCollections(selectedWs.id)
      .then((data) => {
        if (stale) return;
        const cols = Array.isArray(data) ? data : data?.collections || [];
        setWsCollections(cols);
      })
      .catch(() => {
        if (!stale) setWsCollections([]);
      })
      .finally(() => {
        if (!stale) setLoadingCols(false);
      });
    return () => { stale = true; };
  }, [info, workspaceId, workspaces]);

  const handlePasswordCheck = () => {
    setPasswordError('');
    if (!password.trim()) { setPasswordError('Please enter the password'); return; }
    setPasswordOk(true);
  };

  // Save collection data into an existing local workspace snapshot
  const saveCollectionLocally = async (localWorkspace, shareData) => {
    const localId = localWorkspace.local_uid || localWorkspace.id;
    const snap    = await loadSnapshot(localId);
    const newCol  = { id: crypto.randomUUID(), ...shareData.data };
    await requireLocalApiRoom(countApiItems([newCol]), { id: localWorkspace.id, collections: snap.collections });
    await saveSnapshot(localId, { collections: [...(snap.collections || []), newCol] });
  };

  // Save a single shared request into a "Shared Requests" collection in a
  // local workspace snapshot (mirrors the server-side fallback destination).
  const saveRequestLocally = async (localWorkspace, shareData) => {
    const localId = localWorkspace.local_uid || localWorkspace.id;
    const snap    = await loadSnapshot(localId);
    const collections = [...(snap.collections || [])];
    const newReq = { id: crypto.randomUUID(), ...shareData.data };

    let target = collections.find((c) => c.name === 'Shared Requests');
    // One request, plus the "Shared Requests" collection when it is new.
    await requireLocalApiRoom({ collections: target ? 0 : 1, requests: 1 }, { id: localWorkspace.id, collections });
    if (target) {
      const idx = collections.indexOf(target);
      collections[idx] = { ...target, requests: [...(target.requests || []), newReq] };
    } else {
      collections.push({
        id: crypto.randomUUID(),
        name: 'Shared Requests',
        description: '',
        settings: {},
        requests: [newReq],
      });
    }
    await saveSnapshot(localId, { collections });
  };

  // Create a brand-new local workspace containing the given collections.
  // Workspace shares also pass `settings` (the payload's data.settings) so the
  // fork keeps its variables — mapped into the snapshot fields the local store
  // understands (see localWorkspaceStore.normalizeSnapshot).
  const createLocalWorkspaceWith = async (name, description, collections, settings = null) => {
    // Plans v2: accepting into a NEW local workspace counts.
    if (!requireWorkspaceSlot()) {
      throw Object.assign(new Error(workspaceSlotRefusal() || 'plan_limit'), { planLimit: true });
    }
    const newLocalId = generateLocalWorkspaceId();
    const snapshot = {
      workspaceName: name,
      collections: (collections || []).map((c) => ({
        id: crypto.randomUUID(),
        ...c,
      })),
    };
    // Plans v2: a new workspace starts empty, but what it brings still counts.
    await requireLocalApiRoom(countApiItems(snapshot.collections));
    if (Array.isArray(settings?.globals)) snapshot.globalVariables = settings.globals;
    if (Array.isArray(settings?.workspace_globals)) snapshot.workspaceVariables = settings.workspace_globals;
    if (Array.isArray(settings?.environments)) snapshot.environments = settings.environments;
    await saveSnapshot(newLocalId, snapshot);
    const metas = await listLocalWorkspaceMetasFromManifest();
    await saveLocalWorkspaceManifest([
      ...metas,
      {
        id:          newLocalId,
        local_uid:   newLocalId,
        name,
        description: description || '',
        type:        'personal',
        storage:     'local',
        source:      'personal',
        role:        'owner',
      },
    ]);
  };


  /**
   * Fetch the snapshot and decrypt it if it is an envelope. A v0 (plaintext)
   * share — every share minted before encryption shipped, and every non-chat
   * one — comes back untouched, so mixed rows are the normal state here.
   */
  const readShareData = async (pw) => {
    const shareData = await shareService.fetchShareData(token, { password: pw });
    if (!isEncryptedSnapshotData(shareData?.data)) return shareData;
    return { ...shareData, data: await decryptSnapshotData(channelId, shareData.data) };
  };

  /**
   * The cloud branch's version: returns the decrypted share when the payload
   * IS an envelope, and null when the server can still fork it itself.
   *
   * The pre-fetch happens for any snapshot share rather than trusting
   * `encHint` alone. The hint saves the round trip on the common non-snapshot
   * case; letting it decide on its own would mean a card whose marker was lost
   * (an older sender, a hand-built message) silently forking into empty
   * requests, which is the one outcome worse than the disclosure this feature
   * prevents.
   */
  const readEncryptedShareData = async (pw) => {
    if (!info?.snapshot && !encHint) return null;
    const shareData = await shareService.fetchShareData(token, { password: pw });
    if (!isEncryptedSnapshotData(shareData?.data)) return null;
    return { ...shareData, data: await decryptSnapshotData(channelId, shareData.data) };
  };

  const handleAccept = async () => {
    if (!token || !info) return;
    setAccepting(true);
    try {
      const pw = info.password_protected ? password : null;

      // Resolve the selected workspace object (null for the NEW_WS sentinel)
      const selectedWs =
        workspaceId === NEW_WS ? null : workspaces.find((w) => String(w.id) === workspaceId);

      const local = selectedWs
        ? isLocalWs(selectedWs)
        : workspaceId === NEW_WS && newWsKind === 'local';

      if (local) {
        // ── Local save path ──────────────────────────────────────────────────
        const shareData = await readShareData(pw);

        const intoNewLocalWs = workspaceId === NEW_WS && newWsKind === 'local';

        if (info.type === 'collection') {
          if (intoNewLocalWs) {
            await createLocalWorkspaceWith(newWorkspaceName.trim(), '', [shareData.data]);
          } else {
            if (!selectedWs) throw new Error('No local workspace selected');
            await saveCollectionLocally(selectedWs, shareData);
          }
        } else if (info.type === 'request') {
          if (intoNewLocalWs) {
            await createLocalWorkspaceWith(newWorkspaceName.trim(), '', [
              {
                name: 'Shared Requests',
                description: '',
                settings: {},
                requests: [{ id: crypto.randomUUID(), ...shareData.data }],
              },
            ]);
          } else {
            if (!selectedWs) throw new Error('No local workspace selected');
            await saveRequestLocally(selectedWs, shareData);
          }
        } else if (info.type === 'workspace') {
          // New local workspace named by the user (falls back to share name).
          await createLocalWorkspaceWith(
            newWorkspaceName.trim() || shareData.data.name,
            shareData.data.description || '',
            shareData.data.collections || [],
            shareData.data.settings || null
          );
        }
      } else {
        // ── Cloud accept path ────────────────────────────────────────────────
        const creatingWorkspace = workspaceId === NEW_WS;
        const numericId =
          workspaceId && !creatingWorkspace ? Number(workspaceId) : null;
        const requestOpts = {};
        if (info.type === 'request') {
          if (collectionChoice === NEW_COLLECTION) {
            requestOpts.newCollectionName = newCollectionName.trim();
          } else if (collectionChoice !== AUTO_COLLECTION && !creatingWorkspace) {
            requestOpts.collectionId = Number(collectionChoice);
          }
        }
        const newWorkspaceTeamId =
          creatingWorkspace && newWsKind === 'team' && newWsTeamId ? Number(newWsTeamId) : null;

        // `POST /share/:token/accept` forks from the payload SERVER-SIDE, and
        // an encrypted payload is opaque to it — it would create a collection
        // of empty requests and report success. So an encrypted share takes
        // the same fetch-decrypt-create route the local branch above has
        // always taken, pointed at the cloud APIs.
        const encrypted = await readEncryptedShareData(pw);
        if (encrypted) {
          await acceptDecryptedShareIntoCloud({
            type: encrypted.type || info.type,
            data: encrypted.data,
            workspaceId: numericId,
            newWorkspaceName: creatingWorkspace ? newWorkspaceName.trim() : null,
            newWorkspaceTeamId,
            ...requestOpts,
          });
        } else {
          await shareService.acceptShareLink(token, {
            password: pw,
            workspaceId: numericId,
            newWorkspaceName: creatingWorkspace ? newWorkspaceName.trim() : null,
            newWorkspaceTeamId,
            ...requestOpts,
          });
        }
      }

      setAccepted(true);
      const label = TYPE_LABELS[info.type] || info.type;
      showToast?.(`${label} added successfully!`, 'success');
      onWorkspaceAdded?.();

      setTimeout(() => { handleClose(); }, 1500);
    } catch (err) {
      if (err instanceof ShareEnvelopeError) {
        // Never spelled the same way as a password problem or a network
        // failure: "needs a newer version" and "can't be opened on this
        // account" are different problems with different answers, and both are
        // different from "the share is gone".
        setDecryptError(err.message);
        showToast?.(err.message, 'error');
      } else if (err.message.toLowerCase().includes('password')) {
        setPasswordError(err.message);
        setPasswordOk(false);
      } else {
        showToast?.(err.message, 'error');
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleClose = () => {
    setInfo(null);
    setFetchError('');
    setPassword('');
    setPasswordError('');
    setPasswordOk(false);
    setWorkspaceId('');
    setAccepted(false);
    setDecryptError('');
    setCollectionChoice(AUTO_COLLECTION);
    setNewCollectionName('');
    setWsCollections([]);
    setNewWorkspaceName('');
    setNewWsKind('cloud');
    setNewWsTeamId('');
    onClose();
  };

  // Build workspace options for the picker
  const cloudWorkspaces = workspaces.filter((w) => w.id && !isLocalWs(w));
  const localWorkspaces = workspaces.filter((w) => w.id && isLocalWs(w));
  const needsWorkspacePicker =
    info && (info.type === 'collection' || info.type === 'workspace' || info.type === 'request');

  // Request shares: destination collection picker (cloud workspaces only —
  // local saves always land in "Shared Requests").
  const selectedWsIsCloud =
    workspaceId &&
    workspaceId !== NEW_WS &&
    !isLocalWs(workspaces.find((w) => String(w.id) === workspaceId) || {});
  const showCollectionPicker = info?.type === 'request' && selectedWsIsCloud;
  const collectionPickerOptions = [
    { value: AUTO_COLLECTION, label: '"Shared Requests" (automatic)' },
    ...wsCollections.map((c) => ({ value: String(c.id), label: c.name })),
    { value: NEW_COLLECTION, label: 'New collection…' },
  ];

  // Flat options list for CompactSelectPicker
  const isItemShare = info?.type === 'collection' || info?.type === 'request';
  const wsPickerOptions = [
    ...cloudWorkspaces.map((w) => ({ value: String(w.id), label: w.name })),
    ...localWorkspaces.map((w) => ({ value: String(w.id), label: `${w.name} (Local)` })),
    ...(isItemShare ? [{ value: NEW_WS, label: 'New workspace…' }] : []),
  ];
  const creatingNewWs = workspaceId === NEW_WS;
  const newWsKindOptions = [
    { value: 'local', label: 'Local' },
    ...(isAuthenticated ? [{ value: 'cloud', label: 'Cloud' }] : []),
    ...(isAuthenticated && teams.length > 0 ? [{ value: 'team', label: 'Team' }] : []),
  ];

  // Whether the action button should be disabled
  const actionDisabled = (() => {
    if (!info || accepting) return true;
    // An envelope that will not open will not open on a retry either — the
    // key is per-channel and per-account, not a transient.
    if (decryptError) return true;
    if (info.type === 'team') return false;
    if (!needsWorkspacePicker) return false;
    if (creatingNewWs) {
      if (!newWorkspaceName.trim()) return true;
      if (newWsKind === 'team' && !newWsTeamId) return true;
      return false;
    }
    if (!workspaceId) return true;
    if (showCollectionPicker && collectionChoice === NEW_COLLECTION && !newCollectionName.trim()) {
      return true;
    }
    return false;
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={info ? (MODAL_TITLES[info.type] ?? 'Share Link') : 'Share Link'}
      customIcon={<Save size={22} className="text-blue-500" />}
    >
      <div className="space-y-4">

        {/* Loading */}
        {fetching && (
          <div className="flex items-center justify-center py-10">
            <Loader size={22} className="animate-spin text-blue-500" />
          </div>
        )}

        {/* Fetch error */}
        {fetchError && !fetching && (
          <div className="share-note share-note--error">
            <AlertCircle size={15} className="mt-0.5 flex-shrink-0 text-red-500" />
            <p className="share-note-text">{fetchError}</p>
          </div>
        )}

        {info && !fetching && (
          <>
            {/* What's being accepted — rendered the way it looks in the sidebar
                (same preview as the Share in Chat modal / chat card). */}
            <div className="share-note share-note--plain">
              {info.type === 'request' ? (
                <ShareRequestRow method={info.method} name={info.name} />
              ) : (
                <div className="chat-share-req-row">
                  {info.type === 'workspace' ? (
                    <Boxes size={14} className="chat-share-card-typeicon" />
                  ) : info.type === 'team' ? (
                    <Users size={14} className="chat-share-card-typeicon" />
                  ) : (
                    <Folder size={14} className="chat-share-card-typeicon" />
                  )}
                  <span className="chat-share-card-name">{info.name || 'Untitled'}</span>
                </div>
              )}

              {/* OsScroll: `.chat-share-card-tree` bounds the height but no
                  longer sets `overflow-y` itself, so an unwrapped list paints
                  over the rest of the modal. */}
              {info.type === 'collection' && (info.requests || []).length > 0 && (
                <OsScroll className="chat-share-card-tree" defer>
                  {(info.requests || []).map((r, i) => (
                    <ShareRequestRow key={i} method={r.method} name={r.name} />
                  ))}
                </OsScroll>
              )}

              {info.type === 'workspace' && (info.collections || []).length > 0 && (
                <OsScroll className="chat-share-card-tree" defer>
                  {(info.collections || []).map((c, i) => (
                    <div key={i} className="chat-share-req-row">
                      <Folder size={12} className="chat-share-card-typeicon" />
                      <span className="chat-share-req-name">{c.name}</span>
                      <span className="chat-share-row-meta">{c.request_count}</span>
                    </div>
                  ))}
                </OsScroll>
              )}

              <div className="share-muted" style={{ marginTop: 4 }}>
                {TYPE_LABELS[info.type]} · Shared by{' '}
                <span className="share-strong">{info.created_by_name}</span>
                {info.expires_at && (
                  <> · Expires {new Date(info.expires_at).toLocaleDateString()}</>
                )}
              </div>
              {info.password_protected && (
                <p className="mt-1 flex items-center gap-1 share-warn-text">
                  <Lock size={11} /> Password protected
                </p>
              )}
            </div>

            {/* Undecryptable snapshot. The preview above still renders — it
                comes from the share INFO, not the payload — so this reads as
                "here is what was shared, and you cannot open it" rather than
                an empty card. Deliberately worded differently from the
                removal case ("This share was removed by the sender"): a
                recipient must be able to tell a key problem from a deletion,
                which is the mistake `EncryptedAttachment` makes when a 404
                and a bad key land in the same branch. */}
            {decryptError && !accepted && (
              <div className="share-note share-note--warn">
                <AlertCircle size={15} className="mt-0.5 flex-shrink-0 text-amber-500" />
                <p className="share-note-text">{decryptError}</p>
              </div>
            )}

            {/* Success state */}
            {accepted && (
              <div className="share-note share-note--ok share-note--center">
                ✓ {TYPE_LABELS[info.type]} added successfully!
              </div>
            )}

            {/* Password gate */}
            {info.password_protected && !passwordOk && !accepted && (
              <div className="space-y-2">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                  placeholder="Enter password to continue"
                  className="modal-input modal-input-no-focus-outline"
                  onKeyDown={(e) => e.key === 'Enter' && handlePasswordCheck()}
                />
                {passwordError && (
                  <p className="share-error-text">{passwordError}</p>
                )}
                <button
                  type="button"
                  onClick={handlePasswordCheck}
                  disabled={!password.trim()}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Lock size={14} /> Unlock
                </button>
              </div>
            )}

            {/* Workspace picker */}
            {passwordOk && !accepted && needsWorkspacePicker && (
              <div className="space-y-1.5">
                <label className="block share-label">
                  {info.type === 'collection' || info.type === 'request'
                    ? 'Add to workspace'
                    : 'Save as new workspace'}
                </label>
                {/* Workspace shares always create a new workspace — no dropdown,
                    just the kind radios + name below. */}
                {isItemShare &&
                  (wsPickerOptions.length > 0 ? (
                    <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                      <CompactSelectPicker
                        value={workspaceId}
                        onChange={setWorkspaceId}
                        options={wsPickerOptions}
                        aria-label="Select destination workspace"
                      />
                    </div>
                  ) : (
                    <p className="share-note share-note--warn share-note--quiet">
                      No workspaces available. Create a workspace first or sign in to use cloud.
                    </p>
                  ))}
                {creatingNewWs && (
                  <div className="space-y-3" style={{ marginTop: 10 }}>
                    {/* Where should the new workspace live? */}
                    <div className="flex items-center" style={{ gap: 20, marginBottom: 12 }}>
                      {newWsKindOptions.map((opt) => (
                        <label
                          key={opt.value}
                          className="flex cursor-pointer items-center share-option-label"
                          style={{ gap: 7 }}
                        >
                          <input
                            type="radio"
                            name="new-ws-kind"
                            className="checkbox-brand cursor-pointer"
                            checked={newWsKind === opt.value}
                            onChange={() => {
                              setNewWsKind(opt.value);
                              if (opt.value === 'team' && !newWsTeamId && teams[0]?.id) {
                                setNewWsTeamId(String(teams[0].id));
                              }
                            }}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>

                    {newWsKind === 'team' && teams.length > 0 && (
                      <div
                        className="modal-picker"
                        style={{ display: 'flex', width: '100%' }}
                      >
                        <CompactSelectPicker
                          value={newWsTeamId}
                          onChange={setNewWsTeamId}
                          options={teams.map((t) => ({ value: String(t.id), label: t.name }))}
                          aria-label="Select team"
                        />
                      </div>
                    )}

                    <input
                      type="text"
                      value={newWorkspaceName}
                      onChange={(e) => setNewWorkspaceName(e.target.value)}
                      placeholder="New workspace name"
                      autoFocus
                      className="modal-input modal-input-no-focus-outline"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Destination collection picker (request shares, cloud workspace) */}
            {passwordOk && !accepted && showCollectionPicker && (
              <div className="space-y-1.5">
                <label className="block share-label">
                  Into collection
                </label>
                <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
                  <CompactSelectPicker
                    value={collectionChoice}
                    onChange={setCollectionChoice}
                    options={collectionPickerOptions}
                    aria-label="Select destination collection"
                  />
                </div>
                {loadingCols && (
                  <p className="share-muted">Loading collections…</p>
                )}
                {collectionChoice === NEW_COLLECTION && (
                  <input
                    type="text"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    placeholder="New collection name"
                    className="modal-input modal-input-no-focus-outline"
                    style={{ marginTop: 10 }}
                  />
                )}
              </div>
            )}

            {/* Action button */}
            {passwordOk && !accepted && (
              <button
                type="button"
                onClick={handleAccept}
                disabled={actionDisabled}
                className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {accepting ? (
                  <><Loader size={14} className="animate-spin" /><span>Processing…</span></>
                ) : (
                  <span>
                    {info.type === 'team'       ? 'Join Team'
                   : info.type === 'workspace'  ? (newWsKind === 'local' ? 'Save as Local Workspace'
                                                  : newWsKind === 'team' ? 'Save as Team Workspace'
                                                  :                        'Save Workspace')
                   : creatingNewWs              ? (newWsKind === 'local' ? 'Create Local Workspace & Save'
                                                  : newWsKind === 'team' ? 'Create Team Workspace & Save'
                                                  :                        'Create Workspace & Save')
                   : info.type === 'request'    ? 'Save Request'
                   :                              'Save Collection'}
                  </span>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ShareAcceptModal;
